import * as React from "react";
import { Button, Card, Divider, Modal, Popconfirm, Space, Tag, Tooltip } from "antd";
import { CardTitle } from "../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../components/antd/card/CardContent";
import { CloseOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { CardCollapsibleContent } from "../../../../components/antd/card/CardCollapsibleContent";
import { Table } from "../../../../newcomponents/table/table";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { IdentityInput } from "../../../../schema/types/identity/input";
import { v4 as uuidv4 } from "uuid";
import { SelectRolesModal } from "../selectRolesModal";
import { checkCanReach, setCloudaccountLink } from "../../../../store/slices/browserServiceSlice";
import { BrowserServiceModal } from "../browserServiceModal";
import { useDrawerContext } from "../../../../components/antd/drawerContext";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { store } from "../../../../store";
import { cloneDeep } from "lodash";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpDescription } from "../../../gvc/testNewForm/CpDescription";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpArrayString } from "../../../gvc/testNewForm/CpArrayString";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { CpErrorWarningArrayIcon } from "../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";

export const IdentityCreateCloudaccessAzure: React.FC = () => {
  const { id } = useCpFormContext();

  const azureLogoPath = `/resources/logos/azureLogo.png`;

  const drawer = useDrawerContext();

  const isEnabled = useCpFormValue(`azure.isEnabled`);
  const ca = useCpFormValue(`azure.cloudAccount`);
  const roleAssignments = useCpFormValue(`azure.roleAssignments`);

  const [isAddingRoleAssignmentItem, setIsAddingRoleAssignmentItem] = React.useState<number | boolean>(false);

  const onDoneRoleAssignmentItemForm = () => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const itemForm = cloneDeep(values.azure.itemForm);
    const currentRoleAssignments = cloneDeep(values.azure.roleAssignments);
    if (typeof isAddingRoleAssignmentItem !== "boolean") {
      currentRoleAssignments.splice(isAddingRoleAssignmentItem, 1, {
        id: uuidv4(),
        scope: itemForm.scope,
        roles: itemForm.roles.map((r) => r.value),
      });
    } else {
      currentRoleAssignments.push({
        id: uuidv4(),
        scope: itemForm.scope,
        roles: itemForm.roles.map((r) => r.value),
      });
    }
    formSetValue(id, `azure.roleAssignments`, currentRoleAssignments);
    formSetValue(id, `azure.itemForm`, { roles: [], scope: "", scopeHref: "" });
    formTriggerValidation(id);
    setIsAddingRoleAssignmentItem(false);
  };

  const onRemoveRoleAssignmentItem = (index: number) => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const currentRoleAssignments = cloneDeep(values.azure.roleAssignments);
    currentRoleAssignments.splice(index, 1);
    formSetValue(id, `azure.roleAssignments`, currentRoleAssignments);
    formTriggerValidation(id);
  };

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-center`}>
              <div
                style={{
                  background: "var(--identity-azure-logo-drop)",
                  borderRadius: 4,
                  padding: "4px 8px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  aria-label="Azure Cloud Access"
                  style={{ width: "auto", height: 30 }}
                  src={azureLogoPath}
                />
              </div>
              <CpErrorWarningArrayIcon names={["azure"]} />
            </div>
          </CardTitle>
          <CardContent>
            <div className="flex items-center gap-2">
              <CpFormItem>
                <div className="flex items-center gap-2">
                  <CpLabel name={`azure.isEnabled`}>Configure</CpLabel>
                  <CpSwitch name={`azure.isEnabled`} />
                </div>
                <CpErrorWarning name={`azure.isEnabled`} />
              </CpFormItem>
            </div>
            <CardCollapsibleContent collapsed={!isEnabled}>
              <CpFormItem>
                <CpLabel name={`azure.cloudAccount`}>Azure Cloud Account</CpLabel>
                <CpDescription>Azure cloud account is used to authenticate with Azure Services.</CpDescription>
                <CpKindSelect
                  kind="cloudaccount"
                  name={`azure.cloudAccount`}
                  queries={[{ property: "provider", value: "azure" }]}
                  optionValue="name"
                  style={{ maxWidth: 600 }}
                />
                <CpErrorWarning name={`azure.cloudAccount`} />
              </CpFormItem>
              <Divider orientation={"left"} orientationMargin={0}>
                Role Assignments
              </Divider>
              {roleAssignments.length > 0 ? (
                <Table<any>
                  tableId="roleAssigments"
                  data={roleAssignments}
                  hideSettings
                  columns={[
                    { id: "scope", label: "Scope", enableResize: true, size: 200 },
                    {
                      id: "roles",
                      label: "Roles",
                      enableResize: true,
                      size: 200,
                      cell: ({ row }) => {
                        const roles = row.original.roles || [];
                        return (
                          <div className="flex flex-wrap gap-2">
                            {roles.map((role) => (
                              <Tag key={role}>{role}</Tag>
                            ))}
                          </div>
                        );
                      },
                    },
                    {
                      id: "action",
                      label: "",
                      enableResize: false,
                      size: 120,
                      cell: ({ row }) => {
                        return (
                          <div className="w-full flex justify-end">
                            <Space.Compact>
                              <Button
                                htmlType={`button`}
                                onClick={async () => {
                                  const itemToEdit = roleAssignments[row.index];
                                  if (itemToEdit) {
                                    formSetValue(id, `azure.itemForm`, {
                                      roles: itemToEdit.roles.map((r) => ({ id: uuidv4(), value: r })),
                                      scope: itemToEdit.scope,
                                      scopeHref: "",
                                    });
                                  }
                                  setIsAddingRoleAssignmentItem(row.index);
                                }}
                                type={"text"}
                                icon={<EditOutlined />}
                              />
                              <Popconfirm title={"Confirm"} onConfirm={() => onRemoveRoleAssignmentItem(row.index)}>
                                <Button htmlType={`button`} type={"text"} icon={<CloseOutlined />} danger />
                              </Popconfirm>
                            </Space.Compact>
                          </div>
                        );
                      },
                    },
                  ]}
                />
              ) : null}
              <Modal
                open={isAddingRoleAssignmentItem !== false}
                title="Role Assigment"
                okText="Add"
                cancelText="Cancel"
                onCancel={() => {
                  setIsAddingRoleAssignmentItem(false);
                  formSetValue(id, `azure.itemForm`, { roles: [], scope: "", scopeHref: "" });
                }}
                onOk={onDoneRoleAssignmentItemForm}
                destroyOnHidden
              >
                <RoleAssignmentItemFormContent isAddingRoleAssignmentItem={isAddingRoleAssignmentItem} />
              </Modal>
              <Button
                htmlType={`button`}
                className="mt-4"
                type={"dashed"}
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  setIsAddingRoleAssignmentItem(true);
                }}
              >
                Add Role Assignment
              </Button>
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

interface Props {
  isAddingRoleAssignmentItem: number | boolean;
}
const RoleAssignmentItemFormContent: React.FC<Props> = ({ isAddingRoleAssignmentItem }) => {
  const { id } = useCpFormContext();

  const [isSelectingScope, setIsSelectingScope] = React.useState(false);
  const [isSelectingRoles, setIsSelectingRoles] = React.useState(false);

  const dispatch = useAppDispatch();
  const [isCheckingReach, setIsCheckingReach] = React.useState(false);
  const reachError = useAppSelector((state) => state.browserService.reachError);
  const canReach = useAppSelector((state) => state.browserService.canReach);
  const cloudAccountLink = useCpFormValue(`azure.cloudAccount.link`);

  React.useEffect(() => {
    init();
  }, []);

  async function init() {
    setIsCheckingReach(true);
    if (cloudAccountLink) {
      dispatch(setCloudaccountLink(cloudAccountLink));
      await dispatch(checkCanReach()).unwrap();
    }

    setIsCheckingReach(false);
  }

  const scopeHref = useCpFormValue(`azure.itemForm.scopeHref`);
  const roles = useCpFormValue(`azure.itemForm.roles`);

  return (
    <>
      <CpFormItem>
        <div className="mb-2">
          <CpLabel name={`azure.itemForm.scope`}>
            <span>Scope</span>
            <Tooltip
              title={
                cloudAccountLink
                  ? canReach
                    ? "Browse Scopes"
                    : "Selected Cloud Account cannot access cloud"
                  : "Select a Cloud Account in the previous screen to browse scopes."
              }
            >
              <Button
                className="ml-2"
                size={`small`}
                onClick={() => setIsSelectingScope(true)}
                disabled={!cloudAccountLink || !canReach || isCheckingReach}
              >
                Browse
              </Button>
            </Tooltip>
          </CpLabel>
        </div>
        <CpInput name={`azure.itemForm.scope`} />
        <CpErrorWarning name={`azure.itemForm.scope`} />
      </CpFormItem>
      <CpLabel name="skip">
        <span>Roles</span>
        <Tooltip
          title={
            cloudAccountLink
              ? canReach
                ? "Browse Roles"
                : "Selected Cloud Account cannot access cloud"
              : "Select a Cloud Account in the previous screen to browse roles."
          }
        >
          <Button
            className="ml-2"
            size={`small`}
            onClick={() => setIsSelectingRoles(true)}
            disabled={!cloudAccountLink || !canReach || isCheckingReach}
          >
            Browse
          </Button>
        </Tooltip>
      </CpLabel>
      <div className="-mt-2">
        <CpArrayString name={`azure.itemForm.roles`} />
      </div>
      <BrowserServiceModal
        open={isSelectingScope}
        title={"Select Scope"}
        forAzureScope={{ isActive: true, cloudaccountLink: cloudAccountLink }}
        restrictToParent={true}
        onClose={(selections) => {
          if (selections.length > 0) {
            formSetValue(id, `azure.itemForm.scope`, selections[0]._cpln.ref);
            formSetValue(id, `azure.itemForm.scopeHref`, selections[0]._cpln.href || "");
          }
          setIsSelectingScope(false);
        }}
        selectionType={"single"}
      />
      {isSelectingRoles ? (
        <SelectRolesModal
          onClose={(selections) => {
            formSetValue(
              id,
              `azure.itemForm.roles`,
              selections.map((s) => ({ id: uuidv4(), value: s })),
            );
            setIsSelectingRoles(false);
          }}
          itemHref={scopeHref}
          currentSelections={[...roles.map((r) => r.value)]}
          isEditing={isAddingRoleAssignmentItem !== true}
          type={"scope"}
        />
      ) : null}
    </>
  );
};
